/**
 * v1 `flysafe/v1/tenant/<tenant>/aircraft/<id>/producer/aircraft/<n>/telemetry`
 * adapter.
 *
 * v1 forwards raw MAVLink messages on this canonical envelope. Every payload
 * has a `_type` field naming the MAVLink subtype (ATTITUDE, GLOBAL_POSITION_INT,
 * SYS_STATUS, ...). We demux by `_type` and return a partial v2 telemetry
 * update with just the fields that subtype carries.
 *
 * Multiple subtypes per drone arrive at high cadence (5–20 Hz each). The
 * caller is expected to:
 *   1. Hold per-drone state and merge-on-non-null (so a SYS_STATUS update
 *      doesn't wipe the lat/lng a GLOBAL_POSITION_INT just wrote).
 *   2. Debounce emits to ~4 Hz per drone.
 *
 * Backfills these v2 telemetry fields that v1's `networkid/#` doesn't carry:
 *   battery_pct, battery_v, gps_sats, gps_hdop, gps_lock, signal,
 *   flight_mode (real autopilot mode), ops_status (MAV_STATE),
 *   climbRate (more reliable than networkid), groundspeed (more reliable).
 */
import { z } from 'zod';
import type { TelemetryEnvelope } from '@/lib/mqtt/schemas/telemetry';

/** Envelope wrapper for every `flysafe/v1/.../telemetry` message. */
export const MavlinkEnvelope = z.object({
  header: z.object({
    message_type: z.string(),
    aircraft_serial: z.string(),
    timestamp_utc: z.string().optional(),
    tenant_id: z.string().optional(),
    sequence: z.number().optional(),
  }),
  payload: z.object({ _type: z.string() }).passthrough(),
});
export type MavlinkEnvelope = z.infer<typeof MavlinkEnvelope>;

/** Result type: which drone this update is for + the field-level partial. */
export interface MavlinkUpdate {
  droneId: string;
  partial: Partial<TelemetryEnvelope>;
}

/* ------------------ MAVLink → v2 mappers per subtype ------------------ */

/** GLOBAL_POSITION_INT — lat/lon as 1e7-scaled ints, alt/relative_alt in mm. */
function fromGlobalPositionInt(p: Record<string, unknown>): Partial<TelemetryEnvelope> {
  const out: Partial<TelemetryEnvelope> = {};
  if (typeof p.lat === 'number') out.lat = p.lat / 1e7;
  if (typeof p.lon === 'number') out.lng = p.lon / 1e7;
  if (typeof p.alt === 'number') out.asl = p.alt / 1000; // mm → m
  if (typeof p.relative_alt === 'number') {
    out.aglM = p.relative_alt / 1000;
    out.agl = (p.relative_alt / 1000) * 3.28084; // m → ft
  }
  if (typeof p.hdg === 'number' && p.hdg < 36000) {
    out.heading = p.hdg / 100; // cdeg → deg
    out.heading_known = true;
  }
  return out;
}

/** VFR_HUD — groundspeed (m/s), climb (m/s), heading (deg), throttle (%). */
function fromVfrHud(p: Record<string, unknown>): Partial<TelemetryEnvelope> {
  const out: Partial<TelemetryEnvelope> = {};
  if (typeof p.groundspeed === 'number') out.groundspeed = p.groundspeed;
  if (typeof p.climb === 'number') out.climbRate = p.climb;
  if (typeof p.heading === 'number') {
    out.heading = p.heading;
    out.heading_known = true;
  }
  return out;
}

/** SYS_STATUS — battery state, comms drop rate. */
function fromSysStatus(p: Record<string, unknown>): Partial<TelemetryEnvelope> {
  const out: Partial<TelemetryEnvelope> = {};
  if (typeof p.voltage_battery === 'number' && p.voltage_battery > 0) {
    out.battery_v = p.voltage_battery / 1000; // mV → V
  }
  if (typeof p.battery_remaining === 'number') {
    // MAVLink: -1 means estimate not available.
    out.battery_pct = p.battery_remaining < 0 ? null : p.battery_remaining;
  }
  return out;
}

/** GPS_RAW_INT / GPS2_RAW — satellite count, HDOP, fix type. */
function fromGpsRaw(p: Record<string, unknown>): Partial<TelemetryEnvelope> {
  const out: Partial<TelemetryEnvelope> = {};
  if (typeof p.satellites_visible === 'number' && p.satellites_visible >= 0) {
    out.gps_sats = p.satellites_visible;
  }
  // MAVLink eph is "GPS HDOP × 100" (unitless ratio scaled). UINT16_MAX (65535)
  // means "unknown" — guard against it.
  if (typeof p.eph === 'number' && p.eph < 65535) {
    out.gps_hdop = p.eph / 100;
  }
  if (typeof p.fix_type === 'number') {
    // MAVLink GPS_FIX_TYPE: 0/1 = no fix, 2 = 2D, 3 = 3D, 4 = DGPS, 5+ = RTK
    if (p.fix_type >= 3) out.gps_lock = 'Acquired';
    else if (p.fix_type === 2) out.gps_lock = 'Acquiring';
    else out.gps_lock = 'Lost';
  }
  return out;
}

/** RC_CHANNELS_RAW — RSSI 0..254 (255 = unknown). */
function fromRcChannels(p: Record<string, unknown>): Partial<TelemetryEnvelope> {
  const out: Partial<TelemetryEnvelope> = {};
  if (typeof p.rssi === 'number' && p.rssi !== 255) {
    // Map to 0..100 percent
    out.signal = Math.round((p.rssi / 254) * 100);
  }
  return out;
}

/** HEARTBEAT — autopilot custom_mode (ArduPilot) + system_status (MAV_STATE). */
// ArduPilot Plane mode names (most common modes only — others fall back to "MODE N").
export const ARDU_PLANE_MODES: Record<number, string> = {
  0: 'MANUAL', 1: 'CIRCLE', 2: 'STABILIZE', 3: 'TRAINING', 4: 'ACRO',
  5: 'FBWA', 6: 'FBWB', 7: 'CRUISE', 8: 'AUTOTUNE', 10: 'AUTO',
  11: 'RTL', 12: 'LOITER', 13: 'TAKEOFF', 14: 'AVOID_ADSB', 15: 'GUIDED',
  16: 'INITIALISING', 17: 'QSTABILIZE', 18: 'QHOVER', 19: 'QLOITER',
  20: 'QLAND', 21: 'QRTL', 22: 'QAUTOTUNE', 23: 'QACRO', 24: 'THERMAL',
};
export const ARDU_COPTER_MODES: Record<number, string> = {
  0: 'STABILIZE', 1: 'ACRO', 2: 'ALT_HOLD', 3: 'AUTO', 4: 'GUIDED',
  5: 'LOITER', 6: 'RTL', 7: 'CIRCLE', 9: 'LAND', 11: 'DRIFT',
  13: 'SPORT', 14: 'FLIP', 15: 'AUTOTUNE', 16: 'POSHOLD', 17: 'BRAKE',
  18: 'THROW', 19: 'AVOID_ADSB', 20: 'GUIDED_NOGPS', 21: 'SMART_RTL',
  22: 'FLOWHOLD', 23: 'FOLLOW', 24: 'ZIGZAG', 25: 'SYSTEMID', 26: 'AUTOROTATE',
};

function fromHeartbeat(p: Record<string, unknown>): Partial<TelemetryEnvelope> {
  const out: Partial<TelemetryEnvelope> = {};
  // type: 1 = FIXED_WING, 2 = QUADROTOR, 10 = ONBOARD_CONTROLLER, etc.
  if (typeof p.custom_mode === 'number') {
    const t = typeof p.type === 'number' ? p.type : 2;
    // Plane (fixed-wing) gets the plane table; everything else uses copter.
    const table = t === 1 ? ARDU_PLANE_MODES : ARDU_COPTER_MODES;
    out.flight_mode = table[p.custom_mode] ?? `MODE ${p.custom_mode}`;
  }
  if (typeof p.system_status === 'number') {
    // MAV_STATE: 0..3 = boot/standby, 4 = ACTIVE, 5 = CRITICAL, 6 = EMERGENCY
    if (p.system_status === 4) out.ops_status = 'Normal';
    else if (p.system_status === 5) out.ops_status = 'Degraded';
    else if (p.system_status === 6) out.ops_status = 'Non-Normal';
  }
  return out;
}

/** TERRAIN_REPORT — gives terrain_height (m AMSL under aircraft) and
 *  current_height (m above terrain, i.e. true AGL). */
function fromTerrainReport(p: Record<string, unknown>): Partial<TelemetryEnvelope> {
  const out: Partial<TelemetryEnvelope> = {};
  if (typeof p.current_height === 'number') {
    out.aglM = p.current_height;
    out.agl = p.current_height * 3.28084;
  }
  return out;
}

/* ------------------ entry point ------------------ */

const SUBTYPE_MAP: Record<string, (p: Record<string, unknown>) => Partial<TelemetryEnvelope>> = {
  GLOBAL_POSITION_INT: fromGlobalPositionInt,
  VFR_HUD: fromVfrHud,
  SYS_STATUS: fromSysStatus,
  GPS_RAW_INT: fromGpsRaw,
  GPS2_RAW: fromGpsRaw,
  RC_CHANNELS_RAW: fromRcChannels,
  RC_CHANNELS: fromRcChannels,
  HEARTBEAT: fromHeartbeat,
  TERRAIN_REPORT: fromTerrainReport,
  // ATTITUDE, PARAM_VALUE, ADSB_VEHICLE etc. don't currently feed v2's
  // telemetry envelope. Add mappers here when those fields get schema homes.
};

/** Parse a flysafe/v1 telemetry envelope and convert to a partial update.
 *  Returns null for malformed envelopes or subtypes we don't map. */
export function mavlinkToPartialTelemetry(raw: unknown): MavlinkUpdate | null {
  const parsed = MavlinkEnvelope.safeParse(raw);
  if (!parsed.success) return null;
  const env = parsed.data;
  const droneId = env.header.aircraft_serial;
  const mapper = SUBTYPE_MAP[env.payload._type];
  if (!mapper) return null;
  const partial = mapper(env.payload as Record<string, unknown>);
  if (Object.keys(partial).length === 0) return null;
  // Stamp received_at on every update so the "Last update Xs ago" header
  // tracks the most recent MAVLink subtype too.
  partial.received_at = env.header.timestamp_utc ?? new Date().toISOString();
  return { droneId, partial };
}
